/**
 * MenuButton — Single icon button inside the MenuBar pill.
 *
 * Reads `activeSubMenu` from the store to determine the active state.
 * On click: calls `toggleSubMenu(submenuKey)` — closes if already active.
 *
 * Design spec (from 08-ui-screens.md):
 *   Inactive: `w-[45px] h-[45px] rounded-full bg-black/50`  icon: #c5c5c5
 *   Active:   `bg-black` + glow ring                        icon: #ffffff
 *   Hover:    `bg-black`
 *
 * @see docs/ims-brand-frontends/08-ui-screens.md#bottom-menu-bar
 */
import { memo } from 'react';
import { cn } from '../../utils/cn';
import { useAppStore } from '../../store/app.store';
import { useUiTheme } from '../../hooks/useUiTheme';
import type { ActiveSubMenu } from '../../store/app.store';

interface MenuButtonProps {
  /** SVG icon URL served from /app/icons/. */
  icon: string;
  /** Accessible label. */
  label: string;
  /** Which submenu slot this button controls. */
  submenuKey: Exclude<ActiveSubMenu, null>;
  /**
   * Optional alternative icon shown when this button is active.
   * Used for door-animation toggle (open ↔ close door icon).
   */
  iconActive?: string;
  className?: string;
}

export const MenuButton = memo(function MenuButton({
  icon,
  label,
  submenuKey,
  iconActive,
  className,
}: MenuButtonProps) {
  const activeSubMenu = useAppStore((s) => s.activeSubMenu);
  const toggleSubMenu = useAppStore((s) => s.toggleSubMenu);
  const theme = useUiTheme();

  const isActive = activeSubMenu === submenuKey;
  const shownIcon = isActive && iconActive ? iconActive : icon;

  return (
    <button
      onClick={() => toggleSubMenu(submenuKey)}
      aria-pressed={isActive}
      aria-label={label}
      className={cn(
        'w-11 h-11 rounded-full flex items-center justify-center',
        'transition-all duration-200 cursor-pointer',
        isActive ? theme.btnActive : theme.btnInactive,
        className,
      )}
    >
      <img src={shownIcon} alt="" aria-hidden className="w-6.5 h-6.5" />
    </button>
  );
});
